import { Card, Stack, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { EmployeeProgress } from './employee-progress';
import { EmployeeLeavesStatus } from './employee-leaves-status';
import { EmployeeProjectsOverview } from './employee-projects-overview';
import { Strings } from '@app/constants';
import { Scrollbar } from '@app/components';
import { EmployeeAssetsAndTasksPage } from './employee-assets-and-tasks-page';
import { UserCard } from '@app/components/card';
import { useRequestState } from '@app/hooks';
import {
  selectDashboardState,
  selectGetProjectContribution,
  selectGetRemainingTaskState,
} from '@app/modules/dashboard/selecter';
import {
  getDashboardData,
  getProjectContribution,
  getRemainingTasks,
} from '@app/modules/dashboard';
import { getDurationDisplay } from '@app/configs';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { EmployeeOverdueTasks } from './employee-overdueTasks-page';

const EmployeeDashboardPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data: myProfile } = useRequestState({
    stateSelector: selectMyProfileFormState,
  });

  useEffect(() => {
    dispatch(getDashboardData());
    dispatch(getProjectContribution({ userId: myProfile?.id }));
    dispatch(getRemainingTasks({ userId: myProfile?.id }));
  }, [dispatch, myProfile]);

  const { data } = useRequestState({
    stateSelector: selectDashboardState,
    errorShown: true,
  });
  const { data: project } = useRequestState({
    stateSelector: selectGetProjectContribution,
    errorShown: true,
  });
  const { data: remainingTaskData } = useRequestState({
    stateSelector: selectGetRemainingTaskState,
    errorShown: true,
  });
  const fullName: string =
    (String(data?.userForDashboard?.firstName) ?? '') +
    ' ' +
    (String(data?.userForDashboard?.lastName) ?? '');
  return (
    <>
      <Scrollbar>
        <Stack flexDirection={{ sm: 'column', md: 'row' }} mt={2}>
          <Card
            sx={{
              ml: { md: 2, xs: 1 },
              mr: { xs: 1, md: 0 },
              mb: 2,
              width: { md: '60%', sm: '97%' },
              borderRadius: '5px',
              backgroundImage: 'url(/assets/images/dashboard-background.png)',
              backgroundSize: 'auto',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          >
            <Stack fontSize={18} fontWeight={700} m={'20px 0px 4px 20px'}>
              {Strings.common.welcomeBackCaption(fullName)}
            </Stack>
            <Stack direction={'row'} mb={2} justifyContent="space-between" mr={3}>
              <Stack
                width={'350px'}
                m={'15px 0px 15px 20px'}
                color={theme.palette.grey[600]}
                fontSize={14}
              >
                {Strings.employeeDashborad.caption}
              </Stack>
              <Stack justifyContent="center" display={{ xs: 'none', md: 'inline' }} mt={-6}>
                <img
                  src={'/assets/images/dashboard-icon.png'}
                  style={{
                    objectFit: 'contain',
                    height: '200px',
                    width: '200px',
                    alignItems: 'center',
                  }}
                />
              </Stack>
            </Stack>
          </Card>

          <Stack sx={{ width: { md: '40%', xs: '98%' } }} mr={{ md: 2, xs: 1 }}>
            <Stack direction={{ md: 'row', xs: 'row' }} width={{ xs: '100%' }}>
              <UserCard
                title={Strings.dashboard.employeeId}
                value={data?.userForDashboard?.userIdString}
                iconImg="ic_employeeId"
                width={{ md: '100%', xs: '50%' }}
              />
              <UserCard
                title={Strings.dashboard.workedFor}
                value={getDurationDisplay(data?.workedFor)}
                iconImg="ic_workedFor"
                width={{ md: '100%', xs: '50%' }}
              />
            </Stack>

            <Stack flexDirection={{ md: 'row', xs: 'row' }}>
              <UserCard
                title={Strings.dashboard.department}
                iconImg="ic_department"
                workedFor={data?.userForDashboard?.department}
                width={{ md: '100%', xs: '50%' }}
              />
              <UserCard
                title={Strings.dashboard.designation}
                value={data?.userForDashboard?.designation}
                iconImg="ic_designation"
                width={{ md: '100%', xs: '50%' }}
              />
            </Stack>
          </Stack>
        </Stack>
        <EmployeeProjectsOverview data={data} />
        <EmployeeAssetsAndTasksPage data={data} remainingTaskData={remainingTaskData} />
        <EmployeeOverdueTasks />
        <EmployeeProgress holidays={data?.holidays} project={project} />
        <EmployeeLeavesStatus data={data} />
      </Scrollbar>
    </>
  );
};

export { EmployeeDashboardPage };
